import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, ArrowLeft, TrendingUp, TrendingDown, Minus,
  ChevronRight,
} from 'lucide-react';
import { eggWatchAdminAPI, EggWatchFarmSummary } from '../../../services/eggWatchAdminAPI';
import './EggWatchAdmin.css';

const statusConfig: Record<string, { label: string; color: string }> = {
  excellent: { label: 'Excellent', color: 'green' },
  good: { label: 'Good', color: 'blue' },
  attention: { label: 'Needs Attention', color: 'yellow' },
  not_logging: { label: 'Not Logging', color: 'red' },
};

const EggWatchFarmsList: React.FC = () => {
  const [farms, setFarms] = useState<EggWatchFarmSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) setStatusFilter(s);
  }, [searchParams]);

  useEffect(() => {
    loadFarms();
  }, [statusFilter]);

  const loadFarms = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const res = await eggWatchAdminAPI.getFarms(params);
      setFarms(res.data);
    } catch (err) {
      console.error('Failed to load farms:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadFarms();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') loadFarms();
  };

  const statusCounts = farms.reduce<Record<string, number>>((acc, f) => {
    acc[f.status] = (acc[f.status] || 0) + 1;
    return acc;
  }, {});

  const trendIcon = (trend: string) => {
    if (trend === 'up') return <TrendingUp size={14} />;
    if (trend === 'down') return <TrendingDown size={14} />;
    return <Minus size={14} />;
  };

  const rateClass = (rate: number | null) => {
    if (rate == null) return '';
    if (rate >= 75) return 'excellent';
    if (rate >= 55) return 'good';
    return 'low';
  };

  return (
    <div className="egg-watch-admin">
      <button className="ewa-back-button" onClick={() => navigate('/planner/egg-watch')}>
        <ArrowLeft size={16} /> Back to Egg Watch
      </button>

      <div className="ewa-header">
        <div className="ewa-header-title">
          <h1>All Farms</h1>
          <p className="ewa-header-subtitle">{farms.length} farms enrolled in Egg Watch</p>
        </div>
      </div>

      {/* Status Filter Chips */}
      <div className="ewa-status-summary">
        <div
          className={`ewa-status-chip ${!statusFilter ? 'active' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          All <span className="count">{farms.length}</span>
        </div>
        {Object.entries(statusConfig).map(([key, cfg]) => (
          <div
            key={key}
            className={`ewa-status-chip ${cfg.color} ${statusFilter === key ? 'active' : ''}`}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
          >
            {cfg.label} <span className="count">{statusCounts[key] || 0}</span>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="ewa-filters-bar">
        <div className="ewa-search-input">
          <Search size={16} color="#9ca3af" />
          <input
            type="text"
            placeholder="Search farms..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <button className="ewa-btn-secondary" onClick={handleSearch}>Search</button>
      </div>

      {/* Farms Table */}
      <div className="ewa-card">
        <div className="ewa-table-container">
          <table className="ewa-table">
            <thead>
              <tr>
                <th>Farm</th>
                <th>Hens</th>
                <th>Flocks</th>
                <th>Feed</th>
                <th>Lay Rate</th>
                <th>Trend</th>
                <th>Last Log</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="loading-cell">Loading farms...</td></tr>
              ) : farms.length === 0 ? (
                <tr><td colSpan={9} className="empty-cell">No farms found</td></tr>
              ) : (
                farms.map(farm => (
                  <tr
                    key={farm.id}
                    className="clickable"
                    onClick={() => navigate(`/planner/egg-watch/farms/${farm.id}`)}
                  >
                    <td>
                      <div className="ewa-farm-cell">
                        <div className="farm-name">{farm.name}</div>
                        {farm.location && <div className="farm-location">{farm.location}</div>}
                      </div>
                    </td>
                    <td>{farm.hen_count.toLocaleString()}</td>
                    <td>{farm.flock_count}</td>
                    <td>{farm.feed_formula || '—'}</td>
                    <td>
                      <span className={`ewa-lay-rate ${rateClass(farm.current_lay_rate)}`}>
                        {farm.current_lay_rate != null ? `${farm.current_lay_rate.toFixed(1)}%` : '—'}
                      </span>
                    </td>
                    <td>
                      <span className={`ewa-trend ${farm.trend}`}>
                        {trendIcon(farm.trend)}
                        {farm.trend_pct != null ? ` ${farm.trend_pct > 0 ? '+' : ''}${farm.trend_pct.toFixed(1)}%` : ''}
                      </span>
                    </td>
                    <td>
                      <span className={`ewa-last-log ${farm.days_since_log > 3 ? 'stale' : ''}`}>
                        {farm.last_log_date
                          ? new Date(farm.last_log_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : 'Never'}
                        {farm.days_since_log > 0 && (
                          <span className="ewa-days-ago">({farm.days_since_log}d)</span>
                        )}
                      </span>
                    </td>
                    <td>
                      <span className={`ewa-status-badge ${statusConfig[farm.status]?.color || ''}`}>
                        {statusConfig[farm.status]?.label || farm.status}
                      </span>
                    </td>
                    <td><ChevronRight size={16} className="ewa-row-arrow" /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EggWatchFarmsList;
